"use client";

import { Button } from "@/components/ui/button";
import ContactHeader from "@/components/ui/ContactHeader";
import { contactDefaultValue } from "@/formDefaultValues/form-default-value";
import useCustomReactHookForm from "@/hooks/use-custom-react-hook-form";
import { contactSchema, TContactFormData } from "@/schema/contactSchema";
import { useTranslations } from "next-intl";
import { useState } from "react";
import ContactFormFields from "./ContactFormFields";
import clientApi from "@/utils/clientApi";
import { useWatch } from "react-hook-form";
import { Captcha } from "../../../ui/form/Captcha";
import { showSuccessToast } from "@/utils/showToast";

const ContactForm = ({ lang }: { lang: string }) => {
  const t = useTranslations();
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    isDirty,
    errors,
    reset,
    isSubmitting,
    control,
  } = useCustomReactHookForm({
    formSchema: contactSchema,
    formDefaultValue: contactDefaultValue,
  });

  const watchMessage = useWatch({
    control,
    name: "message",
  });

  const onSubmit = async (data: TContactFormData) => {
    try {
      const response = await clientApi.post("/contact", {
        ...data,
        recaptchaToken,
      });
      setRecaptchaToken(null);
      reset();
      showSuccessToast(response.data.message);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  return (
    <div className="col-span-3 xl:col-span-2 h-full bg-light dark:bg-input/30 dark:shadow-none dark:backdrop-blur-sm py-4 px-6 rounded-2xl ">
      <ContactHeader lang={lang} title="Get In Touch" />

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="mt-6 flex flex-col gap-4"
      >
        <ContactFormFields register={register} errors={errors} />
        {watchMessage && (
          <Captcha lang={lang} setRecaptchaToken={setRecaptchaToken} />
        )}

        <Button
          type="submit"
          aria-label={t("Send Message")}
          disabled={!isDirty || !recaptchaToken || isSubmitting}
          className="w-full"
        >
          {isSubmitting ? t("Loading") : t("Send Message")}
        </Button>
      </form>
    </div>
  );
};

export default ContactForm;
